import { convertChannelCasing } from 'lib-shared-api-utils';
import type { Channel } from 'lib-shared-types-channels';
import isEqual from 'lodash/isEqual';
import {
  createSelector,
  createSelectorCreator,
  defaultMemoize,
} from 'reselect';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { getConversationTitle } from 'utils/conversation';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const getCurrentConversation = (state: RootReducerStateT) =>
  state.conversation.metaConversation;

const getTimelineItems = (state: RootReducerStateT) =>
  state.timeline.timelineItems;

export const getCurrentConversationLanguage = (state: RootReducerStateT) =>
  getCurrentConversation(state).conversation.attributes?.language;

export const getCurrentConversationId = createSelector(
  [getCurrentConversation],
  (metaConversation) => {
    return metaConversation?.conversation.id;
  },
);

const getCurrentConversationDate = createSelector(
  [getCurrentConversation],
  (metaConversation) => {
    return metaConversation?.conversation.created_at;
  },
);

const getCurrentConversationRequester = createSelector(
  [getCurrentConversation],
  (metaConversation) => {
    return metaConversation?.requester;
  },
);

export const getTimelineBasic = createSelector(
  [getTimelineItems, getCurrentConversationId, getCurrentConversationRequester],
  (timelineItems, currentId, requester) => {
    if (!timelineItems) return null;
    return timelineItems.map(
      ({ id, status, initial_channel, created_at, direction, attributes }) => {
        return {
          id,
          is_closed: status === 'closed',
          channel: initial_channel,
          date: created_at,
          hasFollowUp: status === 'pending' || status === 'awaitingpending',
          isCurrent: currentId === id,
          status,
          title: getConversationTitle({
            channel: convertChannelCasing(initial_channel, 'snake') as Channel,
            direction,
            subject: attributes?.subject,
            requester,
          }),
        };
      },
    );
  },
);

export const getLatestFacebookConversation = createSelector(
  [getTimelineItems],
  (timelineItems) => {
    if (!timelineItems) return null;
    const messengerConversations = timelineItems.filter(
      ({ initial_channel }) => initial_channel === 'facebookmessenger',
    );
    const messengerConversationsSorted = messengerConversations.sort(
      (a, b) => b.id - a.id,
    );
    if (messengerConversationsSorted.length > 0) {
      return messengerConversationsSorted[0].id;
    }
    return null;
  },
);

function findMostImportantStatus(items) {
  return items.reduce((mostImportantStatus, item) => {
    if (mostImportantStatus === 'open') return mostImportantStatus;
    if (!item.is_closed && !item.hasFollowUp) return 'open';
    if (item.hasFollowUp) return 'pending';
    return mostImportantStatus;
  }, 'closed');
}

export const getConversationTimeline = createDeepEqualSelector(
  [getTimelineBasic, getCurrentConversationDate],
  (conversations, currentConversationTime) => {
    if (!conversations || conversations.length < 1) return null;

    const currentConversationDate = currentConversationTime
      ? new Date(currentConversationTime).setHours(0, 0, 0, 0)
      : null;

    const conversationsSorted = conversations.sort((a, b) => a.id - b.id);

    // Get start date
    const dateFirstConversation = new Date(conversationsSorted[0].date);
    const dateLastConversation = new Date(); // new Date(conversationsSorted[conversationsSorted.length - 1].created_at).setHours(0,0,0,0);
    dateFirstConversation.setHours(0, 0, 0, 0);
    dateLastConversation.setHours(0, 0, 0, 0);

    // Generate conversations by date
    const conversationsByDate = new Map();
    conversationsSorted.forEach((conversation) => {
      const dateWithoutTime = new Date(conversation.date).setHours(0, 0, 0, 0);
      const daysFromStart = Math.round(
        (dateWithoutTime - dateFirstConversation.getTime()) / 86400000,
      );
      if (!conversationsByDate.has(daysFromStart)) {
        conversationsByDate.set(daysFromStart, {
          daysFromStart,
          conversations: [],
        });
      }
      conversationsByDate.get(daysFromStart).conversations.push(conversation);
    });

    // Set most important status for each day
    for (const [_, cluster] of conversationsByDate) {
      const status = findMostImportantStatus(cluster.conversations);
      cluster.status = status;
    }

    return {
      dateFirstConversation,
      dateLastConversation,
      conversationsByDate: Array.from(conversationsByDate.values()),
      currentConversationDate,
    };
  },
);

export const getLastEndUserMessage = createSelector(
  [getCurrentConversation, getCurrentConversationRequester],
  (metaConversation, requester) => {
    if (metaConversation) {
      const lastMessageFromEndUser = [...metaConversation.messages]
        .reverse()
        .find(
          (message) =>
            message.author_id === requester?.id &&
            message.direction === 'inbound' &&
            message.type === 'conversation:message_added',
        );

      if (!lastMessageFromEndUser || !lastMessageFromEndUser.text) {
        return null;
      }
      return lastMessageFromEndUser.text;
    }
    return null;
  },
);
